import { Head, Link } from '@inertiajs/react';

/**
 * Issue data from the backend.
 */
interface NewspaperIssue {
    id: number;
    issue_number: number;
    title: string | null;
    display_title: string;
    article_count: number;
    is_published: boolean;
    created_at: string | null;
}

/**
 * Props for the Archives page.
 */
interface Props {
    issues: NewspaperIssue[];
    isGm: boolean;
}

/**
 * Public Newspaper Archives page - list of past published issues.
 */
export default function Archives({ issues, isGm }: Props) {
    return (
        <>
            <Head title="Archives - The Feywild Times" />

            <div className="min-h-screen bg-gray-50">
                {/* Newspaper Header */}
                <header className="bg-white border-b-4 border-double border-gray-900 py-6">
                    <div className="max-w-6xl mx-auto px-4 text-center">
                        <p className="text-sm text-gray-600 font-serif mb-1">Archives</p>
                        <h1 className="text-5xl md:text-6xl font-serif font-bold text-black tracking-tight">
                            <Link href="/newspaper" className="hover:text-gray-700 transition-colors">
                                The Feywild Times
                            </Link>
                        </h1>
                        <p className="text-gray-600 font-serif italic mt-2">
                            "100% Accurate Opinionated Reporting"
                        </p>
                        <div className="flex justify-center gap-4 mt-4 text-sm text-gray-700">
                            <Link
                                href="/newspaper"
                                className="hover:text-gray-500 transition-colors underline"
                            >
                                Latest Issue
                            </Link>
                            <span className="text-gray-400">|</span>
                            <Link
                                href="/newspaper/ask"
                                className="hover:text-gray-500 transition-colors underline"
                            >
                                Ask the Feywild Times
                            </Link>
                            <span className="text-gray-400">|</span>
                            <Link
                                href="/newspaper/comics"
                                className="hover:text-gray-500 transition-colors underline"
                            >
                                Features
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="max-w-4xl mx-auto px-4 py-8">
                    <h2 className="text-2xl font-serif font-bold text-black mb-6 border-b-2 border-gray-900 pb-2">
                        Past Issues
                    </h2>

                    {issues.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-600">
                                No archived issues yet. Check back after more issues are published.
                            </p>
                            <Link
                                href="/newspaper"
                                className="inline-block mt-4 text-gray-700 hover:text-gray-500 underline"
                            >
                                Return to Latest Issue
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {issues.map((issue) => (
                                <Link
                                    key={issue.id}
                                    href={`/newspaper/issue/${issue.id}`}
                                    className="block bg-white border border-gray-300 rounded-lg p-4 hover:shadow-lg transition-shadow"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-xl font-serif font-bold text-black">
                                                {issue.display_title}
                                            </h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {issue.article_count} article{issue.article_count !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            {issue.created_at && (
                                                <p className="text-sm text-gray-500">
                                                    {new Date(issue.created_at).toLocaleDateString()}
                                                </p>
                                            )}
                                            {isGm && !issue.is_published && (
                                                <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-500 text-white rounded text-xs">
                                                    Draft
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </>
    );
}
